import { useEffect, useRef, useState } from 'react';
import { Eyebrow } from '../Eyebrow';
import { formatCurrency } from '../../lib/format';
import { useReducedMotion } from '../../hooks/useReducedMotion';

export interface RangeHeadlineProps {
  low: number;
  high: number;
  /** Small eyebrow above. Defaults to YOUR_INDICATION. */
  eyebrow?: string;
}

const DURATION_MS = 900;

function easeOut(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

export interface CounterProps {
  target: number;
  reduced: boolean;
}

export function Counter({ target, reduced }: CounterProps) {
  const [value, setValue] = useState(reduced ? target : 0);
  const frame = useRef<number | null>(null);

  useEffect(() => {
    if (reduced) {
      setValue(target);
      return;
    }
    const start = performance.now();
    const from = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / DURATION_MS);
      setValue(from + (target - from) * easeOut(t));
      if (t < 1) {
        frame.current = requestAnimationFrame(tick);
      }
    };
    frame.current = requestAnimationFrame(tick);
    return () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    };
  }, [target, reduced]);

  return <>{formatCurrency(value)}</>;
}

export function RangeHeadline({ low, high, eyebrow = 'YOUR_INDICATION' }: RangeHeadlineProps) {
  const reduced = useReducedMotion() ?? false;

  return (
    <div>
      <Eyebrow>{eyebrow}</Eyebrow>
      <p
        className="mt-6 font-display font-light font-tnum leading-[1.02] tracking-[-0.02em]"
        style={{ fontSize: 'clamp(3rem, 8vw, 5.5rem)' }}
      >
        <span data-testid="range-low">
          <Counter target={low} reduced={reduced} />
        </span>
        <span className="mx-3 text-ink-subtle font-extralight text-[0.5em]">to</span>
        <span data-testid="range-high">
          <Counter target={high} reduced={reduced} />
        </span>
      </p>
      <p className="mt-5 text-[0.9375rem] text-ink-muted max-w-xl">
        Best-case to conservative. Your indication is a range. A broker will follow up with the
        formal numbers.
      </p>
    </div>
  );
}
